import type { ApolloError } from '@apollo/client';
import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { brandAndCountryFromZone } from '@seek/audience-zones';

import { CONFIG } from '../../window';

import { beforeSend } from './beforeSend';
import { scrubSensitiveData } from './utils';

export type InputProps = Record<string, any>;

export interface BrowserLogExtras {
  kind?: 'GraphMutationError' | 'GraphQueryError' | 'MiddlewareError';
  name?: string;
  input?: InputProps;
}

const {
  DATADOG_BROWSER_LOG_ENABLED: datadogLoggerEnabled,
  ENV: env,
  version,
  site,
  zone,
} = CONFIG || {};

export const initBrowserLog = (): void => {
  if (datadogLoggerEnabled) {
    // for more info about the configuration:
    // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
    datadogLogs.init({
      service: 'ca-settings-ui',
      clientToken: 'pubd429322fdc5cd2f32c160a2485d8cd74',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env,
      version,
      beforeSend,
    });
  }
};

const getExtrasDetails = (
  extras: BrowserLogExtras,
  error?: Error | ApolloError,
) => {
  const { name, kind, input } = extras;
  const [brand, country] = (zone && brandAndCountryFromZone(zone)) || [];
  return {
    site,
    name,
    kind,
    brand,
    country,
    zone,
    input: input && scrubSensitiveData(input),
    raw: { ...error },
  };
};

const log = (
  level: StatusType,
  error: Error | ApolloError,
  extras: BrowserLogExtras = {},
): void => {
  const extrasDetail = getExtrasDetails(extras, error);
  if (datadogLoggerEnabled) {
    datadogLogs.logger[level](error.message, {
      'error.kind': error.name,
      'error.stack': error.stack,
      extras: extrasDetail,
    });
  } else {
    switch (level) {
      case 'error': {
        // eslint-disable-next-line no-console
        console.error(extrasDetail, error);
        break;
      }
      case 'warn': {
        // eslint-disable-next-line no-console
        console.warn(extrasDetail, error);
        break;
      }
      default: {
        break;
      }
    }
  }
};

export const browserLogError = (
  error: Error | ApolloError,
  extras: BrowserLogExtras = {},
): void => {
  log(StatusType.error, error, extras);
};

export const browserLogInfo = (
  message: string,
  extras: BrowserLogExtras = {},
): void => {
  const extrasDetail = getExtrasDetails(extras);
  if (datadogLoggerEnabled) {
    datadogLogs.logger.log(message, {
      extras: extrasDetail,
    });
  } else {
    // eslint-disable-next-line no-console
    console.log({ message, extrasDetail });
  }
};
